/**
 * Which chrome the app is wearing.
 *
 * Two entry points are in scope: the eShop, which gets the masthead, the
 * breadcrumb bar and the footer, and the MyOrange webview, which gets a single
 * title bar and no site navigation because it is embedded in a native app.
 *
 * The mode is read once from `?context=webview` and then held in
 * `sessionStorage`, because the query string does not survive the first
 * in-app navigation and a shell that flips back to the eShop masthead halfway
 * through the flow would be worse than either mode. Same reasoning as the OTP
 * component's persistence (Q-38): the webview case is defined by the app being
 * left and returned to.
 */
import { Injectable, computed, signal } from '@angular/core';

export type ShellMode = 'eshop' | 'webview';

const STORAGE_KEY = 'pre2post.poc.shell.v1';

@Injectable({ providedIn: 'root' })
export class ShellContext {
  private readonly modeSignal = signal<ShellMode>(initialMode());

  readonly mode = this.modeSignal.asReadonly();
  readonly isWebview = computed(() => this.modeSignal() === 'webview');

  /** The dev overlay switches this. Nothing in the product path does. */
  set(mode: ShellMode): void {
    this.modeSignal.set(mode);
    try {
      sessionStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Private browsing, or storage disabled. The mode still applies for this
      // page load; it simply will not survive a navigation.
    }
  }
}

function initialMode(): ShellMode {
  try {
    const fromUrl = new URLSearchParams(location.search).get('context');
    if (fromUrl === 'webview' || fromUrl === 'eshop') {
      sessionStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(STORAGE_KEY) === 'webview' ? 'webview' : 'eshop';
  } catch {
    return 'eshop';
  }
}
